/**
 * APNs push registration for caregiver sleep alerts (Sleep Suite, v1.2.0).
 *
 * iOS-native only. `@capacitor/push-notifications` is loaded via dynamic
 * `await import()` inside try/catch so web builds and web runtime degrade to
 * silent no-ops. Per the thenable-proxy rule the plugin handle lives only in
 * a plain wrapper object — never awaited, never returned from an async fn.
 *
 * Flow:
 *  - Toggle ON  (NotificationPreferences): `enableCaregiverPush()` — request
 *    permission (only ever from that user toggle), register with APNs, then
 *    POST /user/notifications/devices { token, platform: 'ios', appVersion }.
 *  - Toggle OFF: `disableCaregiverPush()` — DELETE the device row and
 *    unregister locally.
 *  - App start (SleepSuiteServices, once): `ensureRegisteredOnLaunch()` —
 *    if previously enabled and permission still granted, silently re-register
 *    to refresh the server row's lastSeenAt/TTL. Never prompts.
 *
 * Listener ownership (so nothing can double-register):
 *  - This module owns ONLY the transient `registration` / `registrationError`
 *    listeners inside `acquireToken`, and removes them the moment a token,
 *    error or timeout arrives. They are never left attached.
 *  - The delivery listeners (`pushNotificationActionPerformed`,
 *    `pushNotificationReceived`) belong to the SleepSuiteServices driver — it
 *    is the single mounted subscriber that owns tap routing and `requestFullSync`.
 *    It may attach them itself, or use `addPushActionListener` /
 *    `addPushReceivedListener` below, which keep at most ONE native listener
 *    per event however many times they are called (React StrictMode double
 *    mounts included) and detach it when the last subscriber unsubscribes.
 */

import { apiPost, apiDelete, silentFetch } from '../shared/api/client';
import { isIOS, isNative } from '../shared/utils/platform';

const TOKEN_KEY = 'bt_push_token';
const ENABLED_KEY = 'bt_caregiver_push_enabled';
const REGISTER_TIMEOUT_MS = 10000;

function isSupportedPlatform() {
  return isNative() && isIOS();
}

/**
 * True when caregiver push has been enabled on THIS device (local flag set by
 * a successful `enableCaregiverPush`, cleared by `disableCaregiverPush`).
 * The server pref alone is not enough — a device with no registration row
 * can never receive pushes, so UI toggles must AND the server pref with this.
 */
export function isRegistered() {
  try {
    return localStorage.getItem(ENABLED_KEY) === '1';
  } catch {
    return false;
  }
}

let cachedPlugin = null; // { api } | null — plain wrapper, never the raw proxy

async function getPlugin() {
  if (!isSupportedPlatform()) return null;
  if (cachedPlugin) return cachedPlugin;
  try {
    const mod = await import('@capacitor/push-notifications');
    if (!mod?.PushNotifications) return null;
    cachedPlugin = { api: mod.PushNotifications };
    return cachedPlugin;
  } catch {
    return null; // plugin not installed — degrade silently
  }
}

async function getAppVersion() {
  try {
    const { App } = await import('@capacitor/app');
    const info = await App.getInfo();
    return info?.version || '';
  } catch {
    return '';
  }
}

/** Register with APNs and resolve the device token (with timeout). */
function acquireToken(api) {
  return new Promise((resolve, reject) => {
    let done = false;
    const handles = [];
    const cleanup = () => {
      handles.forEach((h) => {
        try {
          h.remove();
        } catch {
          /* already removed */
        }
      });
    };
    const finish = (fn, value) => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      cleanup();
      fn(value);
    };
    const timer = setTimeout(
      () => finish(reject, new Error('Push registration timed out')),
      REGISTER_TIMEOUT_MS
    );
    const keep = (promise) =>
      promise
        .then((h) => {
          if (done) h.remove();
          else handles.push(h);
        })
        .catch(() => {});
    keep(api.addListener('registration', (token) => finish(resolve, token?.value)));
    keep(
      api.addListener('registrationError', (err) =>
        finish(reject, new Error(err?.error || 'Push registration failed'))
      )
    );
    api.register().catch((e) => finish(reject, e));
  });
}

/**
 * Enable caregiver push on this device. Prompts for permission if needed
 * (call ONLY from the user's toggle). Returns
 * { ok: true } | { ok: false, reason: 'unsupported' | 'denied' | 'error' }.
 */
export async function enableCaregiverPush() {
  const plugin = await getPlugin();
  if (!plugin) return { ok: false, reason: 'unsupported' };
  try {
    let perm = await plugin.api.checkPermissions();
    if (perm?.receive !== 'granted') {
      perm = await plugin.api.requestPermissions();
    }
    if (perm?.receive !== 'granted') return { ok: false, reason: 'denied' };

    const token = await acquireToken(plugin.api);
    if (!token) return { ok: false, reason: 'error' };

    const appVersion = await getAppVersion();
    await apiPost('/user/notifications/devices', { token, platform: 'ios', appVersion });

    try {
      localStorage.setItem(TOKEN_KEY, token);
      localStorage.setItem(ENABLED_KEY, '1');
    } catch {
      /* storage unavailable */
    }
    return { ok: true };
  } catch {
    return { ok: false, reason: 'error' };
  }
}

/**
 * Disable caregiver push on this device: delete the server device row and
 * clear local state. Local state clears even if the server call fails —
 * the row's TTL expires server-side anyway.
 */
export async function disableCaregiverPush() {
  let token = null;
  try {
    token = localStorage.getItem(TOKEN_KEY);
  } catch {
    /* storage unavailable */
  }
  try {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(ENABLED_KEY);
  } catch {
    /* storage unavailable */
  }
  if (token) {
    try {
      await apiDelete(`/user/notifications/devices/${encodeURIComponent(token)}`);
    } catch {
      /* best-effort — server TTL cleans up stale rows */
    }
  }
  const plugin = await getPlugin();
  if (plugin) {
    try {
      await plugin.api.unregister();
    } catch {
      /* not fatal */
    }
  }
}

// --- Delivery listeners (driver-facing, single-registration guarded) ---

const ACTION_EVENT = 'pushNotificationActionPerformed';
const RECEIVED_EVENT = 'pushNotificationReceived';

/** event -> Set of subscriber callbacks */
const subscribers = {
  [ACTION_EVENT]: new Set(),
  [RECEIVED_EVENT]: new Set(),
};
/** event -> Promise<listener handle | null>; presence means "already attached" */
const nativeListeners = new Map();

/** Attach the one native listener for `event` (idempotent). */
function ensureNativeListener(event) {
  const existing = nativeListeners.get(event);
  if (existing) return existing;
  const pending = (async () => {
    const plugin = await getPlugin();
    if (!plugin) return null;
    try {
      return await plugin.api.addListener(event, (payload) => {
        subscribers[event].forEach((fn) => {
          try {
            fn(payload);
          } catch {
            /* a bad handler must not break the listener */
          }
        });
      });
    } catch {
      return null;
    }
  })();
  nativeListeners.set(event, pending);
  return pending;
}

function detachNativeListener(event) {
  const pending = nativeListeners.get(event);
  if (!pending) return;
  nativeListeners.delete(event);
  pending
    .then((handle) => {
      try {
        handle?.remove();
      } catch {
        /* already removed */
      }
    })
    .catch(() => {});
}

/** Subscribe `handler` to `event`; resolves with an unsubscribe function. */
async function subscribe(event, handler) {
  if (typeof handler !== 'function' || !subscribers[event]) return () => {};
  subscribers[event].add(handler);
  await ensureNativeListener(event);
  return () => {
    subscribers[event].delete(handler);
    if (subscribers[event].size === 0) detachNativeListener(event);
  };
}

/**
 * Taps on a caregiver push. `onAction(route, notification)` receives the
 * `route` the server put in the APNs payload (iOS surfaces custom keys under
 * `notification.data`). Resolves with an unsubscribe function; a no-op on web.
 */
export async function addPushActionListener(onAction) {
  return subscribe(ACTION_EVENT, (payload) => {
    const notification = payload?.notification || payload || {};
    const route = notification.data?.route ?? notification.extra?.route;
    onAction(typeof route === 'string' ? route : null, notification);
  });
}

/**
 * A push arriving while the app is in the foreground. `onReceived(notification)`
 * — the driver uses this to pull fresh data (`requestFullSync`). Resolves with
 * an unsubscribe function; a no-op on web.
 */
export async function addPushReceivedListener(onReceived) {
  return subscribe(RECEIVED_EVENT, (notification) => onReceived(notification || {}));
}

/**
 * On app start: if caregiver push was previously enabled and permission is
 * still granted, silently re-register and refresh the server device row
 * (lastSeenAt / TTL). Never prompts, never toasts.
 */
export async function ensureRegisteredOnLaunch() {
  if (!isRegistered()) return; // only refresh devices the user explicitly enabled
  const plugin = await getPlugin();
  if (!plugin) return;
  try {
    const perm = await plugin.api.checkPermissions();
    if (perm?.receive !== 'granted') return; // never prompt on launch
    const token = await acquireToken(plugin.api);
    if (!token) return;
    try {
      localStorage.setItem(TOKEN_KEY, token);
    } catch {
      /* storage unavailable */
    }
    const appVersion = await getAppVersion();
    await silentFetch('/user/notifications/devices', 'POST', {
      token,
      platform: 'ios',
      appVersion,
    });
  } catch {
    /* silent — launch path must never surface errors */
  }
}
